import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Search, ShoppingBag, User, Menu, X, ChevronDown } from 'lucide-react';
import { menuStructure, brand } from '../mock';

const LogoIcon = () => (
  <div className="flex items-center gap-2">
    <img src="/etech-logo.png" alt="ETech" className="h-11 w-11 object-contain" />
  </div>
);

const Header = () => {
  const [openIdx, setOpenIdx] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setOpenIdx(null);
  }, [location.pathname]);

  const handleNavClick = (path, e) => {
    if (path.includes('#')) {
      e.preventDefault();
      const [p, hash] = path.split('#');
      if (location.pathname === p) {
        const el = document.getElementById(hash);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } else {
        navigate(p);
        setTimeout(() => {
          const el = document.getElementById(hash);
          if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 200);
      }
    }
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
        scrolled ? 'bg-[#2b2b2b]/95 backdrop-blur-sm shadow-md' : 'bg-[#2b2b2b]'
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="flex items-center justify-between h-20">
          <Link to="/" aria-label={brand.name}>
            <LogoIcon />
          </Link>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-1">
            {menuStructure.map((item, i) => (
              <div
                key={item.label}
                className="relative"
                onMouseEnter={() => item.children && setOpenIdx(i)}
                onMouseLeave={() => setOpenIdx(null)}
              >
                <Link
                  to={item.path.split('#')[0]}
                  onClick={(e) => item.path.includes('#') && handleNavClick(item.path, e)}
                  className={`px-4 py-2 text-[15px] text-[#e9dfc0] hover:text-white transition-colors flex items-center gap-1 ${
                    location.pathname === item.path ? 'text-white' : ''
                  }`}
                >
                  {item.label}
                  {item.children && <ChevronDown className="w-3.5 h-3.5" />}
                </Link>
                {item.children && openIdx === i && (
                  <div className="absolute top-full left-0 pt-2 min-w-[280px] z-50">
                    <div className="bg-[#1e1e1e] border border-[#4a4a4a] rounded-md py-2 shadow-xl">
                      {item.children.map((c) => (
                        <a
                          key={c.label}
                          href={c.path}
                          onClick={(e) => {
                            if (c.path.includes('#')) handleNavClick(c.path, e);
                            else {
                              e.preventDefault();
                              navigate(c.path);
                            }
                          }}
                          className="block px-5 py-2.5 text-[14px] text-[#e9dfc0]/90 hover:text-white hover:bg-[#2b2b2b] transition-colors"
                        >
                          {c.label}
                        </a>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </nav>

          <div className="flex items-center gap-4">
            <button aria-label="Search" className="text-[#e9dfc0] hover:text-white transition-colors hidden sm:block">
              <Search className="w-5 h-5" />
            </button>
            <button aria-label="Account" className="text-[#e9dfc0] hover:text-white transition-colors hidden sm:block">
              <User className="w-5 h-5" />
            </button>
            <button aria-label="Cart" className="text-[#e9dfc0] hover:text-white transition-colors hidden sm:block">
              <ShoppingBag className="w-5 h-5" />
            </button>
            <button
              aria-label="Menu"
              className="lg:hidden text-[#e9dfc0]"
              onClick={() => setMobileOpen(!mobileOpen)}
            >
              {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="lg:hidden bg-[#1e1e1e] border-t border-[#4a4a4a] max-h-[calc(100vh-5rem)] overflow-y-auto">
          <nav className="px-6 py-4 flex flex-col">
            {menuStructure.map((item) => (
              <div key={item.label} className="border-b border-[#4a4a4a]/50 py-2">
                <Link
                  to={item.path.split('#')[0]}
                  className="block py-2 text-[#e9dfc0] text-[16px]"
                >
                  {item.label}
                </Link>
                {item.children && (
                  <div className="pl-4 pb-2">
                    {item.children.map((c) => (
                      <a
                        key={c.label}
                        href={c.path}
                        onClick={(e) => {
                          if (c.path.includes('#')) handleNavClick(c.path, e);
                          else {
                            e.preventDefault();
                            navigate(c.path);
                          }
                        }}
                        className="block py-1.5 text-[#e9dfc0]/70 text-[14px]"
                      >
                        {c.label}
                      </a>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;
